import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { ThemeProvider, useTheme } from 'styled-components';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
// --- THEME DEFINITION (ENHANCED & MERGED) ---
const theme = {
  colors: {
    primary: '#0A7CFF',
    primaryDarker: '#0056b3',
    secondary: '#00E6A4',
    secondaryDarker: '#00b383',
    accent: '#FF4081',
    accentDarker: '#c2185b',

    dssBlue: '#00A9E0', // Retained name: nownfc branding will use this theme color
    dssMediumBlue: '#0095C8', // Retained name
    dssDarkBlue: '#0077B6', // Retained name
    dssGradient: 'linear-gradient(135deg, #00A9E0 0%, #0077B6 100%)', // Retained name
    dssFooterGradient: 'linear-gradient(135deg, #121720 0%, #181e28 100%)',

    text: '#1A202C',
    textLight: '#5A6B7B',
    textAgainstDark: '#000000',
    textMuted: '#6c757d',

    background: '#FFFFFF',
    lightGray: '#F8F9FA',
    mediumGray: '#DEE2E6',
    darkSurface: '#10141D',
    dssBackgroundColor: '#F0F2F5', // Retained name
    dssSectionLightGrayBg: '#F8F9FA', // Retained name


    glassBg: 'rgba(255, 255, 255, 0.75)',
    darkGlassBg: 'rgba(16, 20, 29, 0.80)',
    shadow: 'rgba(0, 0, 0, 0.08)',
    shadowDark: 'rgba(0, 0, 0, 0.15)',
    cardShadowLight: '0 5px 15px rgba(0, 0, 0, 0.07)',
    cardShadowMedium: '0 10px 30px rgba(0, 0, 0, 0.10)',
    cardShadowStrong: '0 15px 45px rgba(0, 0, 0, 0.12)',
    phoneShadow: '0 20px 50px rgba(0, 70, 120, 0.22)',


    gold: '#FFD700',
    silver: '#B0BEC5',
    blackMatte: '#212529',
  },
  fonts: {
    main: "'Inter', sans-serif",
    heading: "'Poppins', sans-serif",
  },
  spacing: {
    xs: '8px', sm: '16px', md: '24px', lg: '48px', xl: '80px', xxl: '120px',
    sectionPaddingDesktop: '100px 60px',
    sectionPaddingMobile: '70px 24px',
    dssSectionPadding: '100px 40px', // Retained name for layout consistency
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    smallMobile: '480px',
  },
  borderRadius: '18px',
  dssBorderRadius: '12px', // Retained name for specific border radius style
  transitions: {
    default: '0.4s cubic-bezier(0.25, 0.8, 0.25, 1)',
    fast: '0.25s ease-out',
    smooth: '0.5s cubic-bezier(0.165, 0.84, 0.44, 1)',
  },
  motion: {
    spring: { type: 'spring', stiffness: 260, damping: 28 },
    springQuick: { type: 'spring', stiffness: 380, damping: 30 },
    reveal: {
      hidden: { opacity: 0, y: 50, scale: 0.97 },
      visible: (i = 0) => ({
        opacity: 1, y: 0, scale: 1,
        transition: { delay: i * 0.08, duration: 0.7, ease: [0.6, -0.05, 0.01, 0.99] },
      }),
    },
    textRevealContainer: {
      hidden: { opacity: 1 },
      visible: {
        opacity: 1,
        transition: { staggerChildren: 0.035, delayChildren: 0.15 }
      }
    },
    textRevealLetter: {
      hidden: { opacity: 0, y: 20, rotateX: -25, filter: 'blur(2px)' },
      visible: { opacity: 1, y: 0, rotateX: 0, filter: 'blur(0px)', transition: { duration: 0.6, ease: [0.6, -0.05, 0.01, 0.99] }}
    }
  },
};

// CORRECTED AnimatedText Component
const AnimatedText = ({ text, el: ElFromProp = 'span', charVariants = theme.motion.textRevealLetter, containerVariants = theme.motion.textRevealContainer, className, ...props }) => {
  let MotionElToUse;
  if (typeof ElFromProp === 'string') {
    MotionElToUse = motion[ElFromProp];
  } else {
    MotionElToUse = ElFromProp;
  }

  if (!MotionElToUse || typeof MotionElToUse === 'object') {
    console.error(`AnimatedText: Invalid 'el' prop. It resolved to '${MotionElToUse}' for ElFromProp: '${ElFromProp}'. Defaulting to motion.span.`);
    MotionElToUse = motion.span;
  }

  return (
    <motion.div variants={containerVariants} className={className} {...props} style={{display: 'inline-block', ...props.style}}>
      {text.split("").map((char, index) => (
        <MotionElToUse key={`${char}-${index}`} variants={charVariants} style={{display: 'inline-block'}}>
          {char === " " ? "\u00A0" : char}
        </MotionElToUse>
      ))}
    </motion.div>
  );
};
// --- ANIMATED SECTION WRAPPER ---
const AnimatedSectionWrapper = ({ children, className, customVariants, threshold = 0.15, as = 'section', id, staggerChildren = 0.08, ...props }) => {
  const controls = useAnimation();
  const [ref, inView] = useInView({ threshold, triggerOnce: true });

  useEffect(() => {
    if (inView) controls.start("visible");
  }, [controls, inView]);

  const defaultVariants = theme.motion.reveal;
  const MotionComponent = motion[as];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: staggerChildren > 0 ? staggerChildren : undefined,
        delayChildren: staggerChildren > 0 ? (customVariants?.visible?.transition?.delayChildren || 0.15) : undefined,
      }
    }
  };

  const itemVariants = customVariants || defaultVariants;

  if (staggerChildren > 0 && React.Children.count(children) > 1) {
    return (
      <MotionComponent
        id={id}
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
        className={`section-container ${className || ''}`}
        {...props}
      >
        {React.Children.map(children, (child, index) => {
          if (React.isValidElement(child)) {
            const isHtmlTag = typeof child.type === 'string';
            const isAlreadyMotionComponent = child.type?.displayName?.startsWith('motion.');

            if (isHtmlTag || (!isAlreadyMotionComponent && typeof child.type === 'function')) {
              // Wrap HTML tags or non-motion functional components
              const MotionChildWrapper = motion[child.type] || motion.div;
              return <MotionChildWrapper key={index} variants={itemVariants} custom={index}>{child.props.children}</MotionChildWrapper>;
            } else if (isAlreadyMotionComponent) {
                // If it's already a motion component, pass variants and custom
                 return React.cloneElement(child, { variants: itemVariants, custom: index, key: child.key || index });
            }
          }
          return child;
        })}
      </MotionComponent>
    );
  }


  return (
    <MotionComponent
      id={id}
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={itemVariants}
      className={`section-container ${className || ''}`}
      {...props}
    >
      {children}
    </MotionComponent>
  );
};


// --- SVG ICONS (MERGED) ---
const Icon = ({ name, size = 24, color, style: propStyle, className, viewBox = "0 0 24 24" }) => {
  const currentTheme = useTheme();

  const iconPaths = {
    menu: <path d="M3 12h18M3 6h18M3 18h18" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>,
    close: <path d="M18 6L6 18M6 6l12 12" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>,
    nfcSignal: <><path d="M14.5 10.5C14.5 9.12 13.38 8 12 8C10.62 8 9.5 9.12 9.5 10.5V13.5C9.5 14.88 10.62 16 12 16C13.38 16 14.5 14.88 14.5 13.5V10.5Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M17.5 9C17.5 7.07 15.93 5.5 14 5.5C12.07 5.5 10.5 7.07 10.5 9V15C10.5 16.93 12.07 18.5 14 18.5C15.93 18.5 17.5 16.93 17.5 15V9Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M20.5 7.5C20.5 5.29 18.71 3.5 16.5 3.5C14.29 3.5 12.5 5.29 12.5 7.5V16.5C12.5 18.71 14.29 20.5 16.5 20.5C18.71 20.5 20.5 18.71 20.5 16.5V7.5Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
    checkCircle: <><path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.00005 12L10.8134 13.8133L15 9.62668" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
    palette: <><path d="M12 21.0001C16.9706 21.0001 21 16.9706 21 12.0001C21 7.02952 16.9706 3.00006 12 3.00006C7.02944 3.00006 3 7.02952 3 12.0001C3 16.9706 7.02944 21.0001 12 21.0001Z" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/><path d="M12 21.0001C12 18.0001 12 15.0001 9.5 13.0001C7 11.0001 5.5 10.5001 4 9.00006C2.5 7.50006 2 6.00006 2 6.00006" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/><path d="M16.5 3.41016C14.5 6.41016 14 9.00008 14 12.0001C14 14.0001 14.5 15.5001 15.5 17.0001" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/></>,
    tap: <><path d="M18.7256 10.4629C18.7256 10.4629 20.1686 11.0119 20.8166 11.7859C21.8106 12.9869 21.4706 14.8349 20.0596 15.7229L16.8936 17.8889C16.0756 18.4449 15.0006 18.3389 14.3016 17.6399L10.0146 13.3519C9.31558 12.6529 9.20958 11.5779 9.76558 10.7599L11.9316 7.59391C12.8196 6.18291 14.6676 5.84291 15.8686 6.83691C16.6426 7.48491 17.1916 8.92791 17.1916 8.92791"/><path d="M12.9688 13.6914L10.0148 10.7374"/><path d="M7.19141 20.3516C7.19141 20.3516 4.80841 19.5006 3.64841 17.8736C2.40441 16.1216 3.02241 13.8046 4.72041 12.6196L8.80841 9.66565" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></>,
    bolt: <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>,
    business: <><path d="M3 21h18"/><path d="M5 21V5C5 4.44772 5.44772 4 6 4H18C18.5523 4 19 4.44772 19 5V21"/><path d="M9 10V12"/><path d="M9 15V17"/><path d="M15 10V12"/><path d="M15 15V17"/><path d="M12 8V21"/></>,
    shield: <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>,
    metal: <><rect x="3" y="3" width="18" height="18" rx="2" ry="2" strokeWidth="1.5"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18" strokeWidth="1.5"/></>,
    pvc: <><rect x="2" y="7" width="20" height="10" rx="1" strokeWidth="1.5"/><path d="M6 10h4M6 14h8" strokeWidth="1.5"/></>,
    cardStack: <><rect x="2" y="10" width="20" height="12" rx="2" ry="2" strokeWidth="1.5"/><path d="M6 6h16a2 2 0 012 2v10M10 2h12a2 2 0 012 2v10" strokeWidth="1.5"/></>,
    truck: <><rect x="1" y="3" width="15" height="13" rx="1" strokeWidth="1.5"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8" strokeWidth="1.5"/><circle cx="5.5" cy="18.5" r="2.5" strokeWidth="1.5"/><circle cx="18.5" cy="18.5" r="2.5" strokeWidth="1.5"/></>,
    settings: <><path d="M19.43 12.98c.04-.32.07-.64.07-.98s-.03-.66-.07-.98l2.11-1.65c.19-.15.24-.42.12-.64l-2-3.46c-.12-.22-.39-.3-.61-.22l-2.49 1c-.52-.4-1.08-.73-1.69-.98l-.38-2.65C14.46 2.18 14.25 2 14 2h-4c-.25 0-.46.18-.49.42l-.38 2.65c-.61.25-1.17.59-1.69.98l-2.49-1c-.23-.09-.49 0-.61.22l-2 3.46c-.13.22-.07.49.12.64l2.11 1.65c-.04.32-.07.65-.07.98s.03.66.07.98l-2.11 1.65c-.19.15-.24.42.12-.64l2 3.46c.12.22.39.3.61.22l2.49 1c.52.4 1.08.73 1.69.98l.38 2.65c.03.24.24.42.49.42h4c.25 0 .46-.18.49.42l.38-2.65c.61-.25 1.17-.59 1.69.98l2.49 1c.23.09.49 0 .61.22l2-3.46c.12-.22-.07-.49-.12-.64l-2.11-1.65zM12 15.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z"/></>,
    linkedin: <><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></>,
    twitter: <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"/>,
    instagram: <><rect x="2" y="2" width="20" height="20" rx="5" ry="5"/><path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></>,
    arrowRight: <path d="M5 12h14m-7-7l7 7-7 7" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"/>,
    qrCode: <><path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm8-12v8h8V3h-8zm6 6h-4V5h4v4zm0 8h.01M13 13h8v8h-8v-8zm2 2h4v4h-4v-4z"/></>,
    check: <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" fill="currentColor" stroke="none"/>,
    customize: <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor" stroke="none"/>,
    dashboard: <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z" fill="currentColor" stroke="none"/>,
    scan: <><path d="M3 5v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2zm16 14H5V5h14v14zM7 7h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2zM7 11h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2zM7 15h2v2H7zm4 0h2v2h-2zm4 0h2v2h-2z" fill="currentColor" stroke="none"/></>,
    link: <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z" fill="currentColor" stroke="none"/>,
    nfc: <><path d="M20 2H4c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 18H4V4h16v16zM11.29 7.71 8.5 10.5l2.79 2.79c.39.39 1.02.39 1.41 0 .39-.39.39-1.02 0-1.41L10.62 11H15c.55 0 1-.45 1-1s-.45-1-1-1h-4.38l1.08-1.08c.39-.39.39-1.02 0-1.41a.9959.9959 0 0 0-1.41 0z" fill="currentColor" stroke="none"/></>,
    qr: <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zm8-12v8h8V3h-8zm6 6h-4V5h4v4zm-6 8h8v-8h-8v8zm2-6h4v4h-4v-4z" fill="currentColor" stroke="none"/>,
    scratch: <><path d="M20.5 3.95a.75.75 0 0 0-1.061-.001L3.95 19.439a.75.75 0 0 0 .001 1.061l.53.53a.75.75 0 0 0 1.061-.001L20.999 5.561a.75.75 0 0 0-.001-1.061l-.53-.531zm-5.743 3.808l-2.828 2.828-1.06-1.06 2.828-2.828 1.06 1.06zM12 2C6.48 2 2 6.48 2 2s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8z" fill="currentColor" stroke="none"/></>,
    secure: <><path d="M18 8h-1V6c0-2.76-2.24-5-5-5S7 3.24 7 6v2H6c-1.1 0-2 .9-2 2v10c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V10c0-1.1-.9-2-2-2zm-6-3.5c1.93 0 3.5 1.57 3.5 3.5V8H8.5V6c0-1.93 1.57-3.5 3.5-3.5zm6 13.5H6V10h12v8z"/><path d="M12 17c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2z" fill="currentColor" stroke="none"/></>,
    nownfcLogoIconSVG: <path d='M50 10 C30 10, 10 30, 10 50 C10 70, 30 90, 50 90 C70 90, 90 70, 90 50 C90 30, 70 10, 50 10 Z M50 20 C61.0457 20 70 28.9543 70 40 C70 51.0457 61.0457 60 50 60 C38.9543 60 30 51.0457 30 40 C30 28.9543 38.9543 20 50 20 Z M50 65 C63.8071 65 75 76.1929 75 90 L25 90 C25 76.1929 36.1929 65 50 65 Z' fill='currentColor'/>,
    facebook: <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />,
    youtube: <path d="M21.58 7.19c-.23-.86-.91-1.54-1.77-1.77C18.25 5 12 5 12 5s-6.25 0-7.81.42c-.86.23-1.54.91-1.77 1.77C2 8.75 2 12 2 12s0 3.25.42 4.81c.23.86.91 1.54 1.77 1.77C5.75 19 12 19 12 19s6.25 0 7.81-.42c.86.23 1.54-.91 1.77-1.77C22 15.25 22 12 22 12s0-3.25-.42-4.81zM9.75 15.02V8.98l5.74 3.02-5.74 3.02z"/>,
    tiktok: <path d="M12.53.02C13.84 0 15.14.01 16.44 0c.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.73v2.1h-2.01c-.04.88-.2 1.75-.46 2.59-.26.84-.58 1.64-.97 2.39-.41.77-.87 1.51-1.39 2.18-.52.66-1.09 1.29-1.72 1.84-.62.55-1.29 1.04-2 1.47-.69.42-1.42.77-2.17 1.05V14.88c.89-.19 1.75-.56 2.53-1.08.79-.53 1.48-1.19 2.04-1.95.57-.75 1.01-1.6 1.32-2.49.3-.89.46-1.84.49-2.83h-2.12V0H12.53v.02z"/>,
    pinterest: <path d="M12 2C6.48 2 2 6.48 2 12c0 4.02 2.67 7.41 6.24 8.66-.06-.28-.11-.79-.11-1.19 0-1.03.24-2.1.7-2.99.39-.75.89-1.36 1.51-1.9.3-.26.48-.55.48-.86 0-.6-.33-1.09-.78-1.09-.33 0-.59.25-.59.56 0 .24.12.49.28.71.03.05.03.07.02.12-.06.21-.17.43-.17.66 0 .46.23.86.65.86.55 0 1.01-.55 1.01-1.34 0-.99-.64-1.71-1.47-1.71-.99 0-1.78.77-1.78 1.83 0 .51.2.86.45 1.13.06.07.07.09.05.18-.09.38-.29.78-.29 1.12 0 .44.13.79.53.79.29 0 .43-.33.43-.59 0-.34-.2-.68-.28-.93-.03-.09.03-.15.09-.12.56.24 1.06.78 1.06 1.61 0 1.01-.69 1.84-1.74 1.84-.85 0-1.57-.65-1.57-1.55 0-.69.43-1.13.68-1.43.01-.01.01-.02 0-.03-.29-.29-.45-.65-.45-1.13 0-.9.63-1.97 2.03-1.97 1.63 0 2.78 1.18 2.78 2.59 0 1.52-.97 2.73-2.33 2.73-.49 0-.94-.25-.94-.55 0-.29.19-.48.34-.63.2-.19.37-.34.37-.58 0-.41-.22-.75-.63-.75-.52 0-.9.45-.9.99 0 .3.07.51.17.68L9.36 17c-.2.78-.9 1.78-2.06 2.45C8.57 21.08 10.23 22 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/>,
  };

  const iconColorResolved = color || (currentTheme ? currentTheme.colors.text : 'currentColor');
  const filledIconNames = ['check', 'customize', 'dashboard', 'scan', 'link', 'nfc', 'qr', 'scratch', 'secure', 'nownfcLogoIconSVG', 'facebook', 'youtube', 'tiktok', 'pinterest'];

  const iconStroke = filledIconNames.includes(name) ? 'none' : iconColorResolved;
  const iconFill = filledIconNames.includes(name) ? iconColorResolved : 'none';

  if (['phone', 'email', 'website', 'address', 'instagramOld', 'twitterOld', 'linkedinOld'].includes(name)) {
    const textIcons = {
        phone: '📞', email: '📧', website: '🌐', address: '📍',
        instagramOld: 'Ig', twitterOld: 'Tw', linkedinOld: 'In',
    };
    const style = {fontSize: `${size*0.7}px`, fontWeight: 600, color: iconColorResolved, ...propStyle, fontFamily: theme.fonts.main};
    if (name === 'instagramOld') {
        style.color = 'transparent';
        style.background = 'radial-gradient(circle at 30% 107%, #fdf497 0%, #fdf497 5%, #fd5949 45%,#d6249f 60%,#285AEB 90%)';
        style.WebkitBackgroundClip = 'text';
        style.backgroundClip = 'text';
    }
    if (name === 'twitterOld') style.color = '#1DA1F2';
    if (name === 'linkedinOld') style.color = '#0077b5';
    return <span style={style} className={className}>{textIcons[name]}</span>;
  }

  return (
    <svg className={className} viewBox={viewBox} width={size} height={size} fill={iconFill} stroke={iconStroke} strokeWidth="1.5" style={propStyle}>
      {iconPaths[name] || <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z" fill="currentColor" stroke="none"/>} {/* Default fallback icon */}
    </svg>
  );
};

// --- REUSABLE UI COMPONENTS ---
const Button = ({ children, nownfcStyle, secondary, small, className, ...props }) => {
  const buttonClasses = [
    'button',
    nownfcStyle ? 'button--nownfc' : '', // Changed from button--dss
    secondary ? 'button--secondary' : '',
    small ? 'button--small' : '',
    className || ''
  ].filter(Boolean).join(' ');

  return (
    <motion.button className={buttonClasses} {...props}>
      {children}
    </motion.button>
  );
};

const Card = ({ children, dark, glass, className, ...props }) => {
  const cardClasses = [
    'card',
    dark ? 'card--dark' : '',
    glass ? 'card--glass' : '',
    className || ''
  ].filter(Boolean).join(' ');

  return (
    <motion.div className={cardClasses} {...props}>
      {children}
    </motion.div>
  );
};

// --- PRICING SECTION ---
// --- PRICING SECTION (The part that changes) ---
export default function Pricing() {
  const plans = [
    {
      id: 'starter',
      name: "nownfc Starter PVC",
      price: "499", // Storing as a string for easy use
      period: "/ one-time",
      description: "The perfect launchpad for individuals stepping into the world of smart networking.",
      features: ["1 Premium PVC NFC Card", "Standard Digital Profile Suite", "Unlimited Taps & Shares", "Essential Customization Kit"],
      highlight: false,
      cta: "Choose Starter"
    },
    {
      id: 'pro',
      name: "nownfc Pro Metal",
      price: "1299",
      period: "/ one-time",
      description: "For discerning professionals crafting an unforgettable statement with a luxury metal card.",
      features: ["1 Luxury Metal NFC Card", "Advanced Digital Profile Suite", "Insightful Profile Analytics", "Priority VIP Support"],
      highlight: true,
      cta: "Go Pro Metal"
    },
    {
      id: 'enterprise',
      name: "nownfc Enterprise",
      price: "2500", // Price is 0 because it's a custom quote
      period: "/ one-time",
      description: "Bespoke NFC solutions designed for ambitious teams and enterprises.",
      features: ["Volume Cards (PVC/Metal)", "Team Command Dashboard", "Branded Profiles", "Dedicated Success Manager"],
      highlight: false,
      cta: "Discuss Enterprise"
    }
  ];
  return (
       <ThemeProvider theme={theme}>
        <AnimatedSectionWrapper as="section" id="pricing" className="pricing-container" staggerChildren={0.05}>
            <div className="section-content-wrapper">
                <motion.h2 className="section-title section-title--dark-text text-center">
                    <AnimatedText text="Smart Value, Smarter Networking" />
                </motion.h2>
                <motion.p className="section-subtitle text-center">
                    Select your nownfc passport to unparalleled networking. Transparent value, transformative impact.
                </motion.p>
                <motion.div className="pricing-grid">
                    {plans.map((plan) => (
                    <Card key={plan.name} className={`pricing-card ${plan.highlight ? 'pricing-card--highlighted' : ''}`}>
                        <div className="plan-name">{plan.name}</div>
                        <div className="plan-price">
                          {plan.id === 'enterprise' ? '2500' : `₹${plan.price}`}
                          <span className="period">{plan.period}</span>
                        </div>
                        <p className="plan-description">{plan.description}</p>
                        <ul>
                        {plan.features.map(feature => (
                            <li key={feature}><Icon name="checkCircle" size={20}/>{feature}</li>
                        ))}
                        </ul>
                        
                        {/* *** CHANGE 2: Use Link to navigate and pass data *** */}
                        <Link to={`/booking?plan=${encodeURIComponent(plan.name)}&price=${plan.price}`}>
                          <Button 
                            nownfcStyle={!plan.highlight} 
                            secondary={!plan.highlight} 
                            whileHover={{scale: 1.04, y: -2}} 
                          >
                            {plan.cta}
                          </Button>
                        </Link>

                    </Card>
                    ))}
                </motion.div>
            </div>
        </AnimatedSectionWrapper>
    </ThemeProvider>
  );
};

